import React from 'react';
import styled from 'styled-components';
import { string, func, bool, number } from 'prop-types';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import {
  API_DURATION_FORMAT,
  formatDisplayDuration,
  formatTime,
  parseAPIDay,
  SHORT_DATE_FORMAT,
} from '@airasia-common/libraries/datetime';

import Image from '../Image';
import { BodyText, CaptionText } from '../Text/Heading';
import Text from '../Text/Text';
import Button from '../Button';
import Wrapper from '../Wrapper';
import Row from '../Container/Row';
import UnlimitedLabel from '../UnlimitedLabel';
import useWindowSize from '../../utils/windowSize';

import { logo } from '../../utils/statics';

const AirAsiaLogo = styled(Image)`
  height: 24px;
  width: 24px;
  object-fit: unset;
  overflow: unset;
`;

const CardWrapper = styled(Row)`
  height: 60px;
  background-color: ${({ theme }) => theme.colors.primaryWhite};
  padding: 16px;
  border-radius: 4px;
`;

const Separator = styled(Wrapper)`
  margin: 11px 8px 0 8px;
  width: 16px;
  border-top: 1px solid black;
`;

const Time = styled(BodyText)`
  font-size: 14px;
`;
Time.displayName = 'Time';

const Location = styled(CaptionText)`
  text-transform: uppercase;
  font-weight: 500;
  line-height: 18px;
`;
Location.displayName = 'Location';

const DatePlus = styled(Text)`
  font-weight: 400;
  font-size: 10px;
  line-height: 18px;
  margin: 5px 0 0 2px;
`;
DatePlus.displayName = 'DatePlus';

const MetaInfo = styled(CaptionText)`
  line-height: 18px;
  max-height: fit-content;
`;
MetaInfo.displayName = 'MetaInfo';

const FlightDetails = styled(Row)`
  flex-direction: column;
  margin-left: 16px;
`;

const FlightCard = ({
  origin,
  destination,
  departTime,
  arrivalTime,
  departDate,
  duration,
  legs,
  days = '0',
  onChange,
  stopsInfoKey,
  isShowStopsInfo,
  isSnapOMSFlightsWeb,
  travel,
  unlimitedPromoPresent,
  airlineCode,
  move,
  ...restProps
}) => {
  const showUnlimitedLabel = unlimitedPromoPresent !== undefined;
  const { isMobileView } = useWindowSize();
  const legsText =
    Number(legs) === 0
      ? translate('flightCard.direct')
      : translate('flightCard.legs', Number(legs));

  const displayDepartDate = !isNaN(Date.parse(departDate))
    ? formatTime(parseAPIDay(departDate), SHORT_DATE_FORMAT)
    : '';
  const stopsInformation = translate('flightCard.stopsLegs', {
    legs: legsText,
  });

  const formatDuration = duration => {
    if (
      typeof duration === 'string' &&
      duration.search(':') &&
      duration !== ''
    ) {
      const [hours, minutes] = duration.split(':').map(Number);
      const formattedDuration = [];
      formattedDuration.push(hours > 0 ? `${hours}h` : '');
      formattedDuration.push(minutes > 0 ? `${minutes}m` : '');
      return formattedDuration.join(' ');
    }
    return '';
  };

  const metaText = translate('flightCard.meta', {
    departDate: displayDepartDate,
    duration: formatDuration(duration),
    legs: stopsInfoKey && isShowStopsInfo ? stopsInformation : legsText,
  });

  const subText =
    isShowStopsInfo && !!stopsInfoKey && Number(legs) > 0
      ? translate('flightCard.stopsLegsInfor', {
          infor: translate(`flightCard.${stopsInfoKey}`),
        })
      : '';

  return (
    <CardWrapper
      {...restProps}
      justifyContent="space-between"
      alignItems="center"
    >
      <Row>
        <AirAsiaLogo
          className="aa-logo"
          src={
            isSnapOMSFlightsWeb
              ? `https://static.airasia.com/flights/images/airlines/logos/64/${airlineCode}.png`
              : logo
          }
        />
        <FlightDetails>
          <Row>
            {travel ? (
              <Text
                fontSize={[14, 14]}
                fontWeight="500"
                lineHeight={['22px', '20px']}
                color={move ? '#023C40' : 'secondary'}
                paddingRight="4px"
                className="trip"
              >
                {translate('tripSummary.depart')}
              </Text>
            ) : (
              <Text
                fontSize={[14, 14]}
                fontWeight="500"
                lineHeight={['22px', '20px']}
                color={move ? '#023C40' : 'secondary'}
                paddingRight="4px"
                className="trip"
              >
                {translate('tripSummary.return')}
              </Text>
            )}
            <Wrapper>
              <Time className="depart-time">{departTime}</Time>
              <Location className="origin">{origin}</Location>
            </Wrapper>
            <Separator />
            <Wrapper>
              <Time className="arrival-time">{arrivalTime}</Time>
              <Location className="destination">{destination}</Location>
            </Wrapper>
            {Number(days) > 0 && (
              <DatePlus className="date-plus">+{days}</DatePlus>
            )}
            {showUnlimitedLabel && !isMobileView && (
              <Wrapper alignSelf="flex-start" paddingLeft="8px" marginTop="3px">
                <UnlimitedLabel isRegular={unlimitedPromoPresent === 0} />
              </Wrapper>
            )}
          </Row>
          <MetaInfo className="meta-text" numberOfLines={1}>
            {metaText}
          </MetaInfo>
          {showUnlimitedLabel && isMobileView && (
            <UnlimitedLabel isRegular={unlimitedPromoPresent === 0} />
          )}
          {isShowStopsInfo && (
            <MetaInfo className="meta-text" numberOfLines={1}>
              {subText}
            </MetaInfo>
          )}
        </FlightDetails>
      </Row>
      {onChange && isSnapOMSFlightsWeb && (
        <Button
          big
          secondary
          style={{
            margin: '10px',
          }}
          onClick={onChange}
          border={move && '#023C40'}
          borderRadius={move && '32px'}
          hovercolor={move && '#E8F2EC'}
        >
          {translate('flightCard.change')}
        </Button>
      )}
      {onChange && !isSnapOMSFlightsWeb && (
        <Text
          fontSize={[14, 16]}
          fontWeight="500"
          lineHeight={['22px', '20px']}
          color="secondary"
          onClick={onChange}
          cursor="pointer"
          className="change-flight"
        >
          {translate('flightCard.change')}
        </Text>
      )}
    </CardWrapper>
  );
};

FlightCard.propTypes = {
  origin: string.isRequired,
  destination: string.isRequired,
  departTime: string.isRequired,
  arrivalTime: string.isRequired,
  departDate: string.isRequired,
  duration: string,
  legs: string.isRequired,
  days: string,
  onChange: func,
  stopsInfoKey: string,
  isShowStopsInfo: bool,
  isSnapOMSFlightsWeb: bool,
  travel: bool,
  unlimitedPromoPresent: number,
  airlineCode: string,
  move: bool,
};

export default FlightCard;
